import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { TbAlertCircle, TbInfoCircle, TbReload } from 'react-icons/tb';
import { Link } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import { useUpdateOrCreateCodePromoMutation } from '@/api/hooks/useCodePromo';
import { RestaurantResponse } from '@/api/lib/restaurant';

import { ActionButton } from '../ActionButton';
import { Spinner } from '@/components/atoms/Spinner';
import { Typography } from '@/components/atoms/Typography';
import { Checkbox } from '@/components/forms/base/Checkbox';
import { TextInput } from '@/components/forms/base/TextInput';
import { Form } from '@/components/forms/core/Form';
import { CodePromotionCard } from '@/components/organisms/CodePromotionCard';
import { useTranslation } from 'react-i18next';

export interface Props {
  restaurant?: RestaurantResponse;
  couponsLength: number;
}

export const HeaderCouponDynamicBlock = ({ restaurant, couponsLength }: Props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const [offerName, setOfferName] = useState('');
  const [codePromo, setCodePromo] = useState('');

  const { isLoading: isCouponAdding, mutateAsync: addCouponMutation } = useUpdateOrCreateCodePromoMutation({});

  const todaysDate = new Date();
  const sevenDaysLater = new Date();
  sevenDaysLater.setDate(todaysDate.getDate() + 7);

  const [startDate, setStartDate] = useState(todaysDate.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(sevenDaysLater.toISOString().split('T')[0]);

  const generateRandomCodePromo = () => {
    const randomCode = `MRH-${Math.floor(1000 + Math.random() * 9000)}`;
    setCodePromo(randomCode);
  };

  useEffect(() => {
    generateRandomCodePromo();
  }, []);

  if (!restaurant) {
    return (
      <div className="flex flex-auto justify-center items-center h-96 md:py-64">
        <Spinner reverse={true} big />
      </div>
    );
  }

  const handleSubmit = async () => {
    setLoading(true);

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) {
      toast.error('Le début ne peut pas être après la fin');
      setLoading(false);
      return;
    }

    const newCodePromoId = uuid();

    const codePromoData = {
      active: true,
      offerName: `${offerName}%`,
      id: newCodePromoId,
      codePromo: codePromo || undefined,
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      restaurantId: restaurant.id,
      appliesOn: undefined,
      conditions: undefined
    };

    try {
      await addCouponMutation({ codePromo: codePromoData, restaurantId: restaurant.id });

      toast.success(t('codePromo.toast.success'));

      setOfferName('');
      setEndDate(sevenDaysLater.toISOString().split('T')[0]);
      generateRandomCodePromo();

      document.getElementById('coupons')?.scrollIntoView({ behavior: 'smooth' });
    } catch (error: any) {
      console.error('Erreur complète :', error);
      if (error.response && error.response.data) {
        console.error("Détails de l'erreur côté serveur :", error.response.data);
        toast.error('Erreur : ' + error.response.data.message || 'Requête invalide.');
      } else {
        toast.error("Une erreur inattendue s'est produite.");
      }
    } finally {
      setLoading(false);
    }
  };

  const thereIsNoCertificate = !restaurant?.digitalCertificate?.suppliers || !restaurant.digitalCertificate?.company;

  return (
    <div className="w-full h-full bg-white grid md:grid-cols-4 gap-5">
      <div className="bg-primary flex-col justify-center gap-5 col-span-2 w-full py-10 px-5 lg:p-10 xl:p-20 items-center flex md:rounded-br-[90px]">
        <CodePromotionCard
          startDate={startDate}
          circleBgColor="bg-primary"
          offerName={`- ${offerName || '10'}%`}
          codePromo={codePromo}
          endDate={endDate ?? null}
          active={true}
          containerClassName="from-gray-400 to-gray-500 border-[3px] border-dashed bg-gradient-to-r"
        />
        <Typography color="white" variant="extraSmall">
          {t('codePromo.example')}
        </Typography>
      </div>

      <div className="space-y-5 m-auto p-5 col-span-2">
        {thereIsNoCertificate && (
          <Link
            to={`/restaurant/certificate/${restaurant.id}`}
            className="flex gap-2 hover:underline underline-offset-2 items-center bg-red-100 w-full rounded-xl p-3"
          >
            <TbAlertCircle size={32} className="text-[#E9001F]" />
            <Typography variant="h4" weight="medium" customColor="#E9001F">
              {t('codePromo.certificateForCodePromo')}
            </Typography>
          </Link>
        )}
        <Typography variant="h2" weight="medium">
          {t('codePromo.newCodeFor')} <strong>{restaurant.name}</strong>
        </Typography>

        <Form onSubmit={handleSubmit} className="space-y-4">
          {/* <div className="flex gap-1">
            <TextInput id="codePromo" label={t('codePromo.labels.codePromo')} defaultValue={codePromo} disabled />
            <ActionButton onClick={generateRandomCodePromo} icon={TbReload} className="mt-6 h-9 w-9" />
          </div> */}

          <div>
            <Typography variant="paragraph" color="black" weight="semibold" marginClassName="mb-1">
              Durée de validité :
            </Typography>
            <Typography variant="paragraph" color="black">
              Les codes promo sont valides pendant 7 jours ⏳
            </Typography>
          </div>

          <div className="gap-2 md:gap-5 border p-3 rounded-lg border-gray-400">
            <div className="flex gap-2 items-center justify-center text-base text-slate-700 font-normal">
              <Typography variant="paragraph" color="black" weight="semibold" marginClassName="mb-0.5">
                Pourcentage de remise
              </Typography>
              <div className="bg-red-500 h-2.5 w-1 rounded-[20px]" />
            </div>
            <div className="flex w-fit mx-auto items-center gap-2 justify-between border border-gray-400 py-0.5 pr-7 pl-8 rounded overflow-auto">
              <span>-</span>
              <input
                required
                type="number"
                value={offerName}
                id={offerName}
                min="0"
                max="100"
                placeholder="10"
                onChange={(e) => setOfferName(e.target.value)}
                className="w-9 border-none focus:border-primary focus:ring-0"
              />
              <span>%</span>
            </div>
          </div>

          <div className="flex gap-2 border-t pt-5">
            <Checkbox id="agree" required />

            <Typography variant="extraSmall" color="gray" marginClassName="mb-1">
              {t('codePromo.declarationCase')} {restaurant.name}. {t('codePromo.engagement')}
            </Typography>
          </div>

          <button
            type="submit"
            className={clsx(
              loading ? 'bg-success cursor-wait' : 'bg-primary hover:bg-shades-primary-hover',
              'disabled:cursor-not-allowed disabled:bg-gray-400 rounded-full px-5 py-1.5 mt-4 w-full duration-300 ease-in-out'
            )}
            disabled={loading || thereIsNoCertificate || couponsLength >= 2 || isCouponAdding}
          >
            <Typography variant="h3" color="white" align="center">
              {loading ? t('codePromo.labels.loading') : t('codePromo.labels.createCoupon')}
            </Typography>
          </button>

          {couponsLength >= 2 && (
            <div className="flex gap-2 items-center bg-blue-100 w-full rounded-xl p-3">
              <TbInfoCircle size={32} className="text-[#0574B8]" />
              <Typography variant="h5" customColor="#0574B8">
                {t('codePromo.maxCoupons')}
              </Typography>
            </div>
          )}
        </Form>
      </div>
    </div>
  );
};
